import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { Origin } from '@classes/origin/origin';
import { UserParameter } from '@classes/user-parameter';
import { User } from '@classes/licence/user';
import { SimulationService } from './simulation.service';
import { FileDetail } from '@classes/file/file-detail';
import { UiService } from './ui.service';
import { FileSource } from '@enums/file-source.enum';
import { ProviderType } from '@enums/provider-type.enum';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    userParametersSubject = new BehaviorSubject<UserParameter>(null);

    private baseUrl = environment.apiUrl + 'users/';

    constructor(
        private http: HttpClient,
        private simuSrv: SimulationService,
        private authSrv: AuthService,
        private uiSrv: UiService
    ) {}

    getParameters(): Observable<UserParameter> {
        return this.http.get<UserParameter>(this.baseUrl + 'parameter').pipe(
            tap(paramsFromApi => {
                this.userParametersSubject.next(paramsFromApi);
            })
        );
    }

    cleanSelectedSimulation() {
        if (this.simuSrv.localSimu)
        {
            this.http.get<boolean>(this.baseUrl + 'cleanSelectedSimulation')
            .subscribe(isValidSimulation => {
                if (!isValidSimulation)
                    this.simuSrv.get().subscribe();
            });
        }
    }

    isSubscribedNewsletter(): Observable<boolean> {
        return this.http.get<boolean>(`${environment.apiUrl}newsletter/subscription`);
    }
    setSubscribedNewsletter(isSubscribed: boolean = false): void {
        var parameter = new NewsletterParameter(isSubscribed);
        this.http.post(`${environment.apiUrl}newsletter/subscription`, parameter).subscribe();
    }
    unsubscribeNewsletter(token: string): Observable<boolean> {
        var parameter = new NewsletterParameter(false, token);
        return this.http.post<boolean>(`${environment.apiUrl}newsletter/unsubscribe`, parameter);
    }

    setParametersOrigin(origin: Origin): void {
        const userParams = this.userParametersSubject.value;
        userParams.origin = origin;

        // Clear organism loaded in ram
        this.simuSrv.clearLocal();
        this.authSrv.refreshToken().subscribe(() => {
            this.userParametersSubject.next(userParams);
        });
    }

    providerTypeMatches(testProvider: ProviderType): boolean {
        const paramsValue = this.userParametersSubject.value;

        const userProvider = paramsValue ? paramsValue.origin.provider.category.code.toLowerCase() : '';
        return userProvider === testProvider.toString();
    }

    setStatus(user: User, method: 'enable' | 'disable'): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}${method}`, user);
    }

    setFileSource(file: FileDetail, source: FileSource): Observable<void> {
        let url = `${this.baseUrl}files/`;

        switch (source) {
            case FileSource.simulation:
                url += 'simulation';
                break;
            case FileSource.provider:
            default:
                url += 'provider';
        }

        this.uiSrv.loadingStateChanged.next(true);
        return this.http.post<void>(url, file).pipe(
            tap(() => {
                this.getParameters().subscribe(() => {
                    this.uiSrv.loadingStateChanged.next(false);
                    this.simuSrv.clearLocal();
                });
            })
        );
    }
}

class NewsletterParameter {
    isSubscribed: boolean;
    token: string;

    constructor(isSubscribed: boolean, token: string = "00000000-0000-0000-0000-000000000000") {
        this.isSubscribed = isSubscribed;
        this.token        = token       ;
    }
}
